import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  InputAdornment, Pagination, CircularProgress, Snackbar, Alert,
  Tooltip, Avatar, Divider, Stack, Switch, Fade, Skeleton,
  LinearProgress, Autocomplete,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AsyncSearchDropdown from "./components/AsyncSearchDropdown";

const BASE_URL = "https://app.mapgeoid.com/api-service";
const OTP_EXPIRE_SECS = 300;

// Static roles served through AsyncSearchDropdown-compatible shape
const STATIC_ROLES = [
  { id: "super_user",      name: "Admin" },
  { id: "staff_user", name: "Staff User" },
  { id: "survey_user",     name: "Surveyor" },
];

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1976d2" },
    secondary: { main: "#9c27b0" },
    background: { default: "#f0f2f5", paper: "#ffffff" },
    success: { main: "#2e7d32" },
    error: { main: "#d32f2f" },
    warning: { main: "#ed6c02" },
    text: { primary: "#1a1a1a", secondary: "#666666" },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e0e0e0" } },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#ffffff",
          color: "#666666",
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          borderBottom: "2px solid #e0e0e0",
        },
        body: { borderBottom: "1px solid #f5f5f5", color: "#1a1a1a" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, fontSize: "0.7rem" } } },
    MuiDialog: {
      styleOverrides: { paper: { background: "#ffffff", border: "none", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } },
    },
  },
});

const PAGE_SIZE = 10;

function stringAvatar(name = "") {
  const parts = name.trim().split(" ");
  const initials = parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : parts[0]?.[0] ?? "?";
  const colors = ["#1976d2", "#388e3c", "#7b1fa2", "#f57c00", "#d32f2f", "#0288d1"];
  const idx = name.charCodeAt(0) % colors.length;
  return {
    sx: { bgcolor: colors[idx], width: 34, height: 34, fontSize: "0.8rem", fontWeight: 700 },
    children: initials.toUpperCase(),
  };
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatTimer(secs) {
  const m = String(Math.floor(secs / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return `${m}:${s}`;
}

// ── Local mock fetch for static roles (no real endpoint needed) ──────────────
// Wraps STATIC_ROLES in the shape AsyncSearchDropdown expects: { items, total_count }
const ROLES_MOCK_URL = "__roles_mock__";
const _origFetch = window.fetch.bind(window);
window.fetch = function (url, ...args) {
  if (typeof url === "string" && url.startsWith(ROLES_MOCK_URL)) {
    const params = new URL(url, "http://x").searchParams;
    const text = (params.get("text") || "").toLowerCase();
    const page = Number(params.get("page") || 1);
    const pageSize = Number(params.get("page_size") || 10);
    const filtered = STATIC_ROLES.filter((r) => r.name.toLowerCase().includes(text));
    const start = (page - 1) * pageSize;
    const items = filtered.slice(start, start + pageSize);
    return Promise.resolve(new Response(JSON.stringify({ items, total_count: filtered.length })));
  }
  return _origFetch(url, ...args);
};

// ── 6-box OTP input ──────────────────────────────────────────────────────────
function OtpInput({ value, onChange, disabled }) {
  const inputs = useRef([]);
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleChange = (i, e) => {
    const ch = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = ch;
    onChange(next.join(""));
    if (ch && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
      const next = [...digits];
      next[i - 1] = "";
      onChange(next.join(""));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted.padEnd(6, "").slice(0, 6));
    inputs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <Stack direction="row" gap={1} justifyContent="center">
      {digits.map((d, i) => (
        <Box
          key={i}
          component="input"
          ref={(el) => (inputs.current[i] = el)}
          value={d}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputMode="numeric"
          maxLength={1}
          sx={{
            width: 44,
            height: 52,
            textAlign: "center",
            fontSize: "1.4rem",
            fontWeight: 700,
            fontFamily: "monospace",
            background: "#f9f9f9",
            border: `2px solid ${d ? "#1976d2" : "#bdbdbd"}`,
            borderRadius: "8px",
            color: "#1a1a1a",
            outline: "none",
            transition: "border-color 0.15s, box-shadow 0.15s",
            caretColor: "#1976d2",
            cursor: disabled ? "not-allowed" : "text",
            opacity: disabled ? 0.45 : 1,
            "&:focus": {
              borderColor: "#1976d2",
              boxShadow: "0 0 0 3px rgba(25,118,210,0.18)",
            },
          }}
        />
      ))}
    </Stack>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Step 1
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", email: "", role: { id: "staff_user", name: "Staff User" }, password: "" });
  const [formErrors, setFormErrors] = useState({});
  const [creating, setCreating] = useState(false);

  // Step 2 – OTP
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [timer, setTimer] = useState(OTP_EXPIRE_SECS);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const startTimer = useCallback(() => {
    setTimer(OTP_EXPIRE_SECS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) { clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  // ── Fetch users ────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const filter = search ? JSON.stringify({ username: search }) : undefined;
      const params = new URLSearchParams({ page_size: PAGE_SIZE, page });
      if (filter) params.set("filter", filter);
      const res = await fetch(`${BASE_URL}/auth/list_users?${params}`, {
        headers: { accept: "application/json" },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (e) {
      notify(`Failed to load users: ${e.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = () => { setPage(1); setSearch(searchInput.trim()); };
  const handleClearSearch = () => { setSearchInput(""); setSearch(""); setPage(1); };

  // ── Toggle active ──────────────────────────────────────────────────────────
  const handleToggleActive = async (user) => {
    setTogglingId(user.id);
    try {
      const res = await fetch(`${BASE_URL}/auth/activate_deactivate_user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: user.id, active: !user.active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      notify(`User "${user.name}" ${!user.active ? "activated" : "deactivated"} successfully`);
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, active: !u.active } : u));
    } catch (e) {
      notify(`Failed to update status: ${e.message}`, "error");
    } finally {
      setTogglingId(null);
    }
  };

  // ── Step 1: Create user ────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.username.trim()) errors.username = "Username is required";
    if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = "Valid email required";
    if (!form.role?.id) errors.role = "Role is required";
    if (form.password.length < 6) errors.password = "Min 6 characters";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;
    setCreating(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/create-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, role: form.role?.id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === "string" ? err.detail : err.detail?.[0]?.msg ?? `HTTP ${res.status}`);
      }
      setCreateOpen(false);
      setOtp("");
      setOtpError("");
      setVerified(false);
      setOtpOpen(true);
      startTimer();
    } catch (e) {
      notify(`Create failed: ${e.message}`, "error");
    } finally {
      setCreating(false);
    }
  };

  // ── Resend OTP ─────────────────────────────────────────────────────────────
  const handleResend = async () => {
    setResending(true);
    setOtp("");
    setOtpError("");
    try {
      const res = await fetch(`${BASE_URL}/auth/create-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, role: form.role?.id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      notify("A new OTP has been sent to the email.", "info");
      startTimer();
    } catch (e) {
      notify(`Resend failed: ${e.message}`, "error");
    } finally {
      setResending(false);
    }
  };

  // ── Step 2: Verify OTP ────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (otp.replace(/\s/g, "").length !== 6) {
      setOtpError("Enter the complete 6-digit OTP");
      return;
    }
    setOtpError("");
    setVerifying(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/verify-email-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", accept: "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: form.email, otp: otp.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === "string" ? err.detail : `HTTP ${res.status}`);
      }
      clearInterval(timerRef.current);
      setVerified(true);
      notify("User verified and created successfully! 🎉");
      fetchUsers();
    } catch (e) {
      setOtpError(e.message || "Invalid or expired OTP");
    } finally {
      setVerifying(false);
    }
  };

  const handleCloseOtp = () => {
    if (verifying) return;
    clearInterval(timerRef.current);
    setOtpOpen(false);
    if (verified) {
      setForm({ name: "", username: "", email: "", role: { id: "staff_user", name: "Staff User" }, password: "" });
      setFormErrors({});
    }
  };

  const handleBackToCreate = () => {
    clearInterval(timerRef.current);
    setOtpOpen(false);
    setCreateOpen(true);
  };

  const timerPct = (timer / OTP_EXPIRE_SECS) * 100;
  const timerColor = timer > 120 ? "#1976d2" : timer > 60 ? "#ed6c02" : "#d32f2f";
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 4 } }}>

        {/* ── Header ── */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" color="text.primary">User Management</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.3}>
              {total} total user{total !== 1 ? "s" : ""} registered
            </Typography>
          </Box>
          <Stack direction="row" gap={1.5}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchUsers} disabled={loading} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <RefreshIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateOpen(true)}>
              Create User
            </Button>
          </Stack>
        </Stack>

        {/* ── Search ── */}
        <Paper sx={{ p: 2, mb: 2.5, bgcolor: "background.paper" }}>
          <Stack direction="row" gap={1.5} alignItems="center">
            <TextField
              placeholder="Search by username…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              size="small" fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined" onClick={handleSearch}
              sx={{ minWidth: 90, borderColor: "#e0e0e0", color: "text.primary", "&:hover": { borderColor: "#1976d2", color: "#1976d2" } }}
            >
              Search
            </Button>
          </Stack>
        </Paper>

        {/* ── Table ── */}
        <Paper sx={{ bgcolor: "background.paper", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="center">Verified</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" sx={{ bgcolor: "#f0f0f0" }} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                  : users.length === 0
                    ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">No users found</Typography>
                        </TableCell>
                      </TableRow>
                    )
                    : users.map((user) => (
                      <Fade key={user.id} in timeout={300}>
                        <TableRow
                          sx={{
                            "&:hover": { bgcolor: alpha("#1976d2", 0.04) },
                            transition: "background 0.15s",
                            opacity: user.active ? 1 : 0.55,
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={1.5}>
                              <Avatar {...stringAvatar(user.name)} />
                              <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">@{user.username}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user.email}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={0.6}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                              <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                                {formatDate(user.last_login)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            {user.is_verified
                              ? <VerifiedIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                              : <Typography variant="body2" color="text.secondary">—</Typography>}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={user.active ? "Active" : "Inactive"}
                              size="small"
                              icon={user.active
                                ? <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                                : <BlockIcon style={{ fontSize: 13 }} />}
                              sx={{
                                bgcolor: user.active ? alpha("#2e7d32", 0.1) : alpha("#d32f2f", 0.1),
                                color: user.active ? "#2e7d32" : "#d32f2f",
                                border: `1px solid ${user.active ? alpha("#2e7d32", 0.3) : alpha("#d32f2f", 0.3)}`,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={user.active ? "Deactivate user" : "Activate user"}>
                              <span>
                                <Switch
                                  size="small"
                                  checked={user.active}
                                  onChange={() => handleToggleActive(user)}
                                  disabled={togglingId === user.id}
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#1976d2" },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#1976d2" },
                                  }}
                                />
                              </span>
                            </Tooltip>
                            {togglingId === user.id && (
                              <CircularProgress size={14} color="primary" sx={{ ml: 0.5 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <>
              <Divider sx={{ borderColor: "#e0e0e0" }} />
              <Stack direction="row" justifyContent="center" p={2}>
                <Pagination
                  count={totalPages} page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary" shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": { color: "text.secondary", borderColor: "#e0e0e0" },
                    "& .Mui-selected": { bgcolor: alpha("#1976d2", 0.12), color: "#1976d2", borderColor: "#1976d2" },
                  }}
                />
              </Stack>
            </>
          )}
        </Paper>

        {/* ════════════════════════════════════════════════════
            STEP 1 — Create User Dialog
        ════════════════════════════════════════════════════ */}
        <Dialog
          open={createOpen}
          onClose={() => { if (!creating) { setCreateOpen(false); setFormErrors({}); } }}
          maxWidth="xs" fullWidth
        >
          <DialogTitle sx={{ pb: 0.5 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <PersonAddIcon color="primary" />
              <Typography variant="h6">Create New User</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={0.4} fontWeight={400}>
              Step 1 of 2 — Fill in details, then verify via OTP.
            </Typography>
          </DialogTitle>
          <Divider sx={{ borderColor: "#e0e0e0", mt: 1.5 }} />
          <DialogContent sx={{ pt: 2.5, pb: 1 }}>
            <Stack gap={2}>
              <TextField
                label="Full Name" value={form.name} size="small" fullWidth
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                error={!!formErrors.name} helperText={formErrors.name}
              />
              <TextField
                label="Username" value={form.username} size="small" fullWidth
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                error={!!formErrors.username} helperText={formErrors.username}
              />
              <TextField
                label="Email" type="email" value={form.email} size="small" fullWidth
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                error={!!formErrors.email} helperText={formErrors.email}
              />
              <AsyncSearchDropdown
                label="Role"
                apiUrl={ROLES_MOCK_URL}
                value={form.role}
                onChange={(val) => setForm({ ...form, role: val })}
                error={!!formErrors.role}
                helperText={formErrors.role}
              />
              <TextField
                label="Password" type="password" value={form.password} size="small" fullWidth
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                error={!!formErrors.password} helperText={formErrors.password}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            <Button
              onClick={() => { setCreateOpen(false); setFormErrors({}); }}
              disabled={creating}
              sx={{ color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained" onClick={handleCreateUser} disabled={creating}
              startIcon={creating ? <CircularProgress size={15} color="inherit" /> : <PersonAddIcon />}
            >
              {creating ? "Sending OTP…" : "Continue →"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ════════════════════════════════════════════════════
            STEP 2 — OTP Verification Dialog
        ════════════════════════════════════════════════════ */}
        <Dialog open={otpOpen} onClose={handleCloseOtp} maxWidth="xs" fullWidth>
          {!verified ? (
            <>
              <DialogTitle sx={{ pb: 0.5 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <LockIcon color="primary" />
                  <Typography variant="h6">Verify Email OTP</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" mt={0.4} fontWeight={400}>
                  Step 2 of 2 — Enter the code sent to your email.
                </Typography>
              </DialogTitle>
              <Divider sx={{ borderColor: "#e0e0e0", mt: 1.5 }} />
              <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Stack gap={3} alignItems="center">

                  {/* User context card */}
                  <Box
                    sx={{
                      width: "100%",
                      bgcolor: alpha("#1976d2", 0.05),
                      border: "1px solid " + alpha("#1976d2", 0.15),
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Avatar {...stringAvatar(form.name)} />
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={700} noWrap>{form.name}</Typography>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <EmailIcon sx={{ fontSize: 11, color: "text.secondary", flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary" noWrap>{form.email}</Typography>
                        </Stack>
                      </Box>
                      <Chip
                        label={form.role?.name ?? form.role?.id ?? "—"}
                        size="small"
                        sx={{
                          bgcolor: alpha("#1976d2", 0.1),
                          color: "#1565c0",
                          border: "1px solid " + alpha("#1976d2", 0.25),
                          fontSize: "0.68rem",
                          flexShrink: 0,
                        }}
                      />
                    </Stack>
                  </Box>

                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Enter the 6-digit code sent to{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>{form.email}</Box>
                  </Typography>

                  {/* OTP boxes */}
                  <OtpInput value={otp} onChange={(v) => { setOtp(v); setOtpError(""); }} disabled={verifying} />

                  {otpError && (
                    <Alert severity="error" sx={{ width: "100%", py: 0.5, fontSize: "0.8rem" }}>
                      {otpError}
                    </Alert>
                  )}

                  {/* Timer */}
                  <Box sx={{ width: "100%" }}>
                    <Stack direction="row" justifyContent="space-between" mb={0.7}>
                      <Typography variant="caption" color="text.secondary">OTP expires in</Typography>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        fontFamily="monospace"
                        sx={{ color: timerColor, transition: "color 0.4s" }}
                      >
                        {timer === 0 ? "Expired" : formatTimer(timer)}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={timerPct}
                      sx={{
                        borderRadius: 4,
                        height: 5,
                        bgcolor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: timerColor,
                          transition: "background-color 0.5s, transform 1s linear",
                        },
                      }}
                    />
                  </Box>

                  {/* Resend */}
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">Didn't receive it?</Typography>
                    <Button
                      size="small"
                      disabled={timer > 0 || resending}
                      onClick={handleResend}
                      sx={{
                        fontSize: "0.75rem",
                        color: timer === 0 ? "#1976d2" : "text.disabled",
                        p: 0, minWidth: 0,
                        "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                      }}
                    >
                      {resending ? "Resending…" : timer > 0 ? `Resend in ${formatTimer(timer)}` : "Resend OTP"}
                    </Button>
                  </Stack>
                </Stack>
              </DialogContent>

              <Divider sx={{ borderColor: "#e0e0e0" }} />
              <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToCreate}
                  disabled={verifying}
                  sx={{ color: "text.secondary", mr: "auto" }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleCloseOtp}
                  disabled={verifying}
                  sx={{ color: "text.secondary" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={verifying || otp.replace(/\s/g, "").length !== 6 || timer === 0}
                  startIcon={verifying ? <CircularProgress size={15} color="inherit" /> : <LockIcon />}
                >
                  {verifying ? "Verifying…" : "Verify & Create"}
                </Button>
              </DialogActions>
            </>
          ) : (
            /* ── Success screen ── */
            <>
              <DialogContent sx={{ pt: 5, pb: 4 }}>
                <Stack alignItems="center" gap={2.5}>
                  <Box
                    sx={{
                      width: 76, height: 76, borderRadius: "50%",
                      bgcolor: alpha("#2e7d32", 0.08),
                      border: "2px solid " + alpha("#2e7d32", 0.3),
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <TaskAltIcon sx={{ fontSize: 42, color: "#2e7d32" }} />
                  </Box>

                  <Box textAlign="center">
                    <Typography variant="h6" fontWeight={700}>Account Created!</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>{form.name}</Box>{" "}
                      has been verified and added to the system.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      bgcolor: alpha("#2e7d32", 0.05),
                      border: "1px solid " + alpha("#2e7d32", 0.15),
                      borderRadius: 2,
                      p: 1.8,
                    }}
                  >
                    <Stack gap={1}>
                      {[
                        ["Username", `@${form.username}`],
                        ["Email", form.email],
                        ["Role", form.role?.name ?? form.role?.id ?? "—"],
                      ].map(([label, val]) => (
                        <Stack key={label} direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography variant="caption" fontWeight={600} color="text.primary">{val}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
                <Button variant="contained" onClick={handleCloseOtp} sx={{ px: 5 }}>
                  Done
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ── Snackbar ── */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4500}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
